import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useAnimal, useAuth, useAnimalMedicalEvents, createAnimalMedicalEvent, deleteAnimalMedicalEvent, toast,
  useShelterVeterinarians, useMedicalEventTitles, MEDICAL_EVENT_ANIMAL_FLAG,
  MEDICAL_EVENT_TYPES, MEDICAL_EVENT_LABELS, vaccinationsForSpecies,
} from '@rehome/shared';
import type { MedicalEventType } from '@rehome/shared';
import { ArrowLeft, Stethoscope, Loader2, Plus, Trash2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { SuggestingInput } from '../components/SuggestingInput';

// ─── AnimalMedicalPage ──────────────────────────────────────────────────────
// Own page (was an inline collapsible panel on AnimalProfilePage — moved out
// to declutter the profile). /shelter/:shelterId/animals/:animalId/medical
// Staff-only, never public — see animal_medical_events (034_animal_medical_care.sql).
// ===========================================================================

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white disabled:bg-gray-50 disabled:text-gray-500';
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

/**
 * Az ivartalanítás egyszeri beavatkozás: ha az állat profilján már be van
 * jelölve, a típus eltűnik a listából. (A régi bejegyzés természetesen
 * megmarad az előzményben — csak újat nem lehet felvenni.)
 */
const selectableEventTypes = (isNeutered: boolean | null | undefined): MedicalEventType[] =>
  MEDICAL_EVENT_TYPES.filter(t => t !== 'Neutering' || isNeutered !== true);
// Purely a visual cue in this panel (the actual reminder lead time is shelter-configured
// server-side, see check-medical-reminders / Beállítások oldal) — a fixed threshold is enough here.
const MEDICAL_DUE_SOON_DAYS = 14;

const emptyMedicalForm = {
  eventType: 'Vaccination' as MedicalEventType,
  title: '', eventDate: new Date().toISOString().split('T')[0],
  veterinarian: '', notes: '',
  isRecurring: false, recurrenceIntervalDays: '' as string, nextDueDate: '' as string, customLeadDays: '' as string,
};

/**
 * `embedded`: az állat adatlapjának `Egészségügy` füleként jelenik meg, nem
 * önálló képernyőként. Ilyenkor elmarad az oldal-keret (vissza-gomb, cím) —
 * azt a befoglaló adatlap adja.
 */
const AnimalMedicalPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { shelterId, animalId } = useParams<{ shelterId: string; animalId: string }>();
  const navigate = useNavigate();
  const { animal, loading, refetch: refetchAnimal } = useAnimal(animalId);
  const { userProfile } = useAuth();
  const { data: medicalEvents, loading: medicalLoading, refetch: refetchMedicalEvents } = useAnimalMedicalEvents(animalId);

  const [medicalForm, setMedicalForm] = useState(emptyMedicalForm);
  /**
   * Az új bejegyzés űrlapja alapból zárva.
   *
   * A gyakori eset a MEGNÉZÉS: a dolgozó az előzményekre kíváncsi. Egy
   * állandóan nyitott, tizenpár mezős űrlap ezt tolja lejjebb, és az
   * adatlap fülére ágyazva végképp elnyomná a listát. A rögzítés így egy
   * szándékos lépés, jól látható gombbal.
   */
  const [showAddForm, setShowAddForm] = useState(false);
  const [savingMedicalEvent, setSavingMedicalEvent] = useState(false);
  // Whether "Következő esedékesség" is still system-computed from
  // "Ismétlődés (nap)" + "Dátum", or the staff has taken manual control of
  // it by editing the date field directly. See handleNextDueDateChange.
  const [nextDueDateAuto, setNextDueDateAuto] = useState(true);
  /**
   * Átálljon-e az állat profilján a típushoz tartozó jelölő (Oltott /
   * Parazitamentes / Ivartalanítva). Alapértelmezés "igen" — ez a tipikus
   * eset —, de a dolgozó "nem"-re állíthatja: egy elhúzódó parazitakezelés
   * első kezelése után az állat még nem parazitamentes.
   */
  const [applyAnimalFlag, setApplyAnimalFlag] = useState(true);
  /**
   * Melyik KONKRÉT oltás állapotát állítsa át a bejegyzés.
   *
   * Az "Oltott" jelölő önmagában nem mondja meg, melyik oltás történt meg —
   * a részletes térkép viszont igen (l. VACCINATIONS). Üresen hagyva csak az
   * összefoglaló jelölő változik, a részletes térkép érintetlen marad.
   */
  const [vaccinationKey, setVaccinationKey] = useState('');

  // Javaslatlisták. Az állatorvos a MENHELY korábbi bejegyzéseiből, a
  // megnevezés a kiválasztott TÍPUS platform-szintű szókincséből jön.
  const { options: vetOptions, loading: vetsLoading } = useShelterVeterinarians(animal?.shelterId);
  const { options: titleOptions, loading: titlesLoading } = useMedicalEventTitles(medicalForm.eventType);

  // Melyik profil-jelölőt érinti a most választott típus (ha bármelyiket).
  const affectedFlag = MEDICAL_EVENT_ANIMAL_FLAG[medicalForm.eventType];
  /** A jelölő jelenlegi értéke az állat profilján. */
  const currentFlagValue = affectedFlag
    ? (animal?.[affectedFlag.field] ?? null)
    : null;
  // Ha a jelölő már a kívánt értéken áll, nincs mit felajánlani.
  const showFlagPrompt = !!affectedFlag && currentFlagValue !== true;

  // Típusváltáskor a felajánlás alapból újra "igen" — különben egy korábbi
  // "nem" döntés némán átragadna a következő, más típusú bejegyzésre.
  const handleEventTypeChange = (value: MedicalEventType) => {
    setMedicalForm(prev => ({ ...prev, eventType: value }));
    setApplyAnimalFlag(true);
    setVaccinationKey('');
  };

  /** Az adott fajnál választható oltások — csak oltás típusnál van értelme. */
  const vaccineOptions = medicalForm.eventType === 'Vaccination' && animal
    ? vaccinationsForSpecies(animal.species)
    : [];

  const canEdit = userProfile?.role === 'shelter_admin' || userProfile?.role === 'super_admin';

  const computeNextDueDate = (eventDate: string, intervalDaysStr: string): string => {
    const days = parseInt(intervalDaysStr, 10);
    if (!eventDate || !(days > 0)) return '';
    const due = new Date(eventDate);
    due.setDate(due.getDate() + days);
    return due.toISOString().split('T')[0];
  };

  // "Ismétlődés (nap)" csak a "Következő esedékesség" előtöltésére szolgál —
  // amíg a staff nem nyúl bele kézzel a dátumba, MINDEN keypress-nél újra
  // kiszámoljuk (korábban csak első alkalommal töltöttük ki, ezért "365"
  // beírásakor a köztes "3"-nál kiszámolt +3 nap ragadt be véglegesen).
  const handleRecurrenceIntervalChange = (value: string) => {
    setMedicalForm(prev => ({
      ...prev,
      recurrenceIntervalDays: value,
      nextDueDate: nextDueDateAuto ? computeNextDueDate(prev.eventDate, value) : prev.nextDueDate,
    }));
  };

  const handleEventDateChange = (value: string) => {
    setMedicalForm(prev => ({
      ...prev,
      eventDate: value,
      nextDueDate: nextDueDateAuto ? computeNextDueDate(value, prev.recurrenceIntervalDays) : prev.nextDueDate,
    }));
  };

  // A staff kézzel írja/választja át a következő esedékességet — mostantól
  // ez az igazi forrás, az ismétlődés napjai többé nem írják felül.
  const handleNextDueDateChange = (value: string) => {
    setNextDueDateAuto(false);
    setMedicalForm(prev => ({ ...prev, nextDueDate: value }));
  };

  const handleResetAutoNextDueDate = () => {
    setNextDueDateAuto(true);
    setMedicalForm(prev => ({ ...prev, nextDueDate: computeNextDueDate(prev.eventDate, prev.recurrenceIntervalDays) }));
  };

  const handleAddMedicalEvent = async () => {
    if (!animalId) return;
    if (!medicalForm.title.trim()) { toast.error('Add meg az esemény megnevezését.'); return; }

    setSavingMedicalEvent(true);
    const created = await createAnimalMedicalEvent({
      animalId,
      eventType: medicalForm.eventType,
      title: medicalForm.title.trim(),
      eventDate: medicalForm.eventDate,
      veterinarian: medicalForm.veterinarian.trim() || null,
      notes: medicalForm.notes.trim() || null,
      isRecurring: medicalForm.isRecurring,
      recurrenceIntervalDays: medicalForm.isRecurring && medicalForm.recurrenceIntervalDays ? parseInt(medicalForm.recurrenceIntervalDays, 10) : null,
      nextDueDate: medicalForm.isRecurring && medicalForm.nextDueDate ? medicalForm.nextDueDate : null,
      customLeadDays: medicalForm.customLeadDays ? parseInt(medicalForm.customLeadDays, 10) : null,
      // Csak akkor írunk a profilra, ha a típus egyáltalán érint jelölőt, a
      // felajánlás látszott, ÉS a dolgozó "igen"-en hagyta.
      animalFlag: affectedFlag && showFlagPrompt && applyAnimalFlag
        ? { column: affectedFlag.column, value: true }
        : null,
      vaccinationKey: medicalForm.eventType === 'Vaccination' && vaccinationKey ? vaccinationKey : null,
    });
    setSavingMedicalEvent(false);
    if (created) {
      setMedicalForm(emptyMedicalForm);
      // Sikeres rögzítés után az űrlap becsukódik: a lista így rögtön
      // látszik a friss bejegyzéssel.
      setShowAddForm(false);
      setNextDueDateAuto(true);
      setApplyAnimalFlag(true);
      setVaccinationKey('');
      refetchMedicalEvents();
      // Az állatot is újratöltjük: ettől frissül a fejléc jelölő-állapota, és
      // ivartalanítás után eltűnik a típus a legördülőből.
      refetchAnimal();
    }
  };

  const handleDeleteMedicalEvent = async (eventId: string) => {
    const ok = await deleteAnimalMedicalEvent(eventId);
    if (ok) refetchMedicalEvents();
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="animate-pulse h-40 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!animal) return <div className="p-8 text-center text-gray-500">Állat nem található.</div>;

  return (
    <div className={embedded ? 'space-y-4' : 'max-w-3xl mx-auto space-y-6 pb-12'}>
{!embedded && (<>
      <button onClick={() => navigate(`/shelter/${shelterId}/animals/${animalId}`)} className="flex items-center gap-2 text-gray-500 hover:text-teal-700 transition-colors">
        <ArrowLeft className="w-4 h-4" />Vissza {animal.name} profiljához
      </button>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Stethoscope className="w-6 h-6 text-blue-600" />
          Egészségügyi adatok — {animal.name}
          <span className="text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">nem publikus</span>
        </h1>
        <p className="text-gray-600 mt-1">Oltások, féreghajtás, allergiák és egyéb egészségügyi bejegyzések</p>
      </div>
      </>)}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          Soha nem jelennek meg az örökbefogadói felületen. Ismétlődő eseményeknél a következő előfordulást
          új bejegyzésként rögzítsd — így megmarad az előzmény.
        </p>

        {medicalLoading && <p className="text-sm text-gray-400">Betöltés...</p>}
        {!medicalLoading && medicalEvents.length === 0 && (
          <p className="text-sm text-gray-400">Még nincs egészségügyi bejegyzés.</p>
        )}
        <div className="space-y-2">
          {medicalEvents.map(ev => {
            const daysUntilDue = ev.nextDueDate
              ? Math.round((new Date(ev.nextDueDate).getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000)
              : null;
            const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
            const isDueSoon = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= MEDICAL_DUE_SOON_DAYS;
            return (
              <div key={ev.id} className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : isDueSoon ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600">{MEDICAL_EVENT_LABELS[ev.eventType]}</span>
                    <p className="font-medium text-gray-800">{ev.title}</p>
                    <span className="text-xs text-gray-400">{ev.eventDate}</span>
                  </div>
                  {ev.veterinarian && <p className="text-xs text-gray-500 mt-0.5">Állatorvos: {ev.veterinarian}</p>}
                  {ev.notes && <p className="text-sm text-gray-600 mt-1">{ev.notes}</p>}
                  {ev.nextDueDate && (
                    <p className={`text-xs font-semibold mt-1 flex items-center gap-1 ${isOverdue ? 'text-red-700' : isDueSoon ? 'text-amber-700' : 'text-gray-500'}`}>
                      {(isOverdue || isDueSoon) && <AlertTriangle className="w-3 h-3" />}
                      {isOverdue ? `Lejárt: ${ev.nextDueDate}` : `Következő esedékesség: ${ev.nextDueDate}`}
                    </p>
                  )}
                </div>
                {canEdit && (
                  <button onClick={() => handleDeleteMedicalEvent(ev.id)} className="text-gray-300 hover:text-red-600 transition-colors" title="Bejegyzés törlése">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {canEdit && (
          <div className="pt-3 border-t border-gray-100 space-y-3">
            {!showAddForm && (
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Új rekord rögzítése
              </button>
            )}

            {showAddForm && (<>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-700">Új rekord rögzítése</h3>
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setMedicalForm(emptyMedicalForm); }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Mégsem
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Típus</label>
                <select value={medicalForm.eventType} onChange={e => handleEventTypeChange(e.target.value as MedicalEventType)} className={inputCls}>
                  {selectableEventTypes(animal.isNeutered).map(t => <option key={t} value={t}>{MEDICAL_EVENT_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="medical-title">Megnevezés</label>
                {/* A javaslatok a KIVÁLASZTOTT TÍPUSHOZ korábban rögzített
                    megnevezésekből jönnek (oltásnál "veszettség", műtétnél
                    "amputáció" …). Nincs beégetett szótár — a felhasználók
                    építik fel, és bármikor írható teljesen új érték is. */}
                <SuggestingInput
                  id="medical-title"
                  value={medicalForm.title}
                  onChange={value => setMedicalForm(prev => ({ ...prev, title: value }))}
                  options={titleOptions}
                  loading={titlesLoading}
                  className={inputCls}
                  placeholder="pl. Veszettség elleni oltás"
                  emptyHint={`Még nincs rögzített megnevezés a(z) "${MEDICAL_EVENT_LABELS[medicalForm.eventType]}" típushoz — írd be az elsőt.`}
                />
              </div>
              <div>
                <label className={labelCls}>Dátum</label>
                <input type="date" value={medicalForm.eventDate} onChange={e => handleEventDateChange(e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="medical-vet">Állatorvos</label>
                {/* A menhelyre járó orvosok fókuszáláskor azonnal megjelennek,
                    gyakoriság szerint — új név bármikor beírható. */}
                <SuggestingInput
                  id="medical-vet"
                  value={medicalForm.veterinarian}
                  onChange={value => setMedicalForm(prev => ({ ...prev, veterinarian: value }))}
                  options={vetOptions}
                  loading={vetsLoading}
                  className={inputCls}
                  placeholder="pl. Dr. Kiss Péter"
                  emptyHint="Még nincs rögzített állatorvos ennél a menhelynél — írd be az elsőt."
                />
              </div>
              <div>
                <label className={labelCls}>Megjegyzés</label>
                <input type="text" value={medicalForm.notes} onChange={e => setMedicalForm(prev => ({ ...prev, notes: e.target.value }))} className={inputCls} placeholder="pl. csirkeallergia, hasmenést okoz" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={medicalForm.isRecurring} onChange={e => { setMedicalForm(prev => ({ ...prev, isRecurring: e.target.checked })); setNextDueDateAuto(true); }} className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
              Ismétlődő esemény (emlékeztetőt kap a menhely, ha esedékessé válik)
            </label>
            {medicalForm.isRecurring && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <div>
                  <label className={labelCls}>Ismétlődés (nap)</label>
                  <input type="number" min="1" value={medicalForm.recurrenceIntervalDays} onChange={e => handleRecurrenceIntervalChange(e.target.value)} className={inputCls} placeholder="pl. 365" />
                  <p className="text-[11px] text-gray-400 mt-1">Csak a következő esedékesség előtöltésére szolgál, magát az emlékeztetőt nem ez indítja.</p>
                </div>
                <div>
                  <label className={labelCls}>Következő esedékesség</label>
                  <input type="date" value={medicalForm.nextDueDate} onChange={e => handleNextDueDateChange(e.target.value)} className={inputCls} />
                  <p className="text-[11px] text-gray-400 mt-1">
                    {nextDueDateAuto
                      ? 'Automatikusan számolva (dátum + ismétlődés). Ez alapján küld emlékeztetőt a rendszer.'
                      : <>Kézzel megadva. <button type="button" onClick={handleResetAutoNextDueDate} className="text-teal-600 hover:underline">Vissza az automatikus számításhoz</button></>}
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Egyedi előrejelzés (nap)</label>
                  <input type="number" min="0" value={medicalForm.customLeadDays} onChange={e => setMedicalForm(prev => ({ ...prev, customLeadDays: e.target.value }))} className={inputCls} placeholder="menhelyi alapértelmezett" />
                  <p className="text-[11px] text-gray-400 mt-1">Hány nappal a fenti dátum előtt szóljon erről az egy bejegyzésről. Üresen a menhelyi alapértelmezett számít (Beállítások).</p>
                </div>
              </div>
            )}
            {/* Profil-jelölő felajánlása. Közvetlenül a mentés gomb ELŐTT,
                mert ez a bejegyzés rögzítésével egy döntés — nem külön
                feladat, amit később valahol máshol el kell végezni. */}
            {showFlagPrompt && affectedFlag && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold">
                      Beállítsuk az állat profilján a(z) „{affectedFlag.label}” mezőt?
                    </p>
                    <p className="text-blue-800/80 text-xs mt-0.5">
                      Jelenlegi érték: <span className="font-medium">
                        {currentFlagValue === false ? 'Nem' : 'Nincs megadva'}
                      </span>. Hosszabb kezelésnél (pl. többlépcsős parazitakezelés)
                      válaszd a „Nem”-et — az állapot csak a kezelés végén igaz.
                    </p>
                  </div>
                </div>
                {/* Oltásnál azt is meg lehet mondani, MELYIK oltásról van szó —
                    így a részletes térkép is naprakész lesz, nem csak az
                    összefoglaló jelölő. */}
                {vaccineOptions.length > 0 && (
                  <div className="pl-6">
                    <label className="block text-xs font-semibold text-blue-900 mb-1" htmlFor="which-vaccine">
                      Melyik oltás?
                    </label>
                    <select
                      id="which-vaccine"
                      value={vaccinationKey}
                      onChange={e => setVaccinationKey(e.target.value)}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 outline-none"
                    >
                      <option value="">Nem konkrét oltáshoz tartozik</option>
                      {vaccineOptions.map(v => (
                        <option key={v.key} value={v.key}>
                          {v.label}
                          {animal?.vaccinations?.[v.key] === 'yes' ? ' — már megvan' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-blue-800/70 mt-1">
                      A kiválasztott oltás az állat profilján „megvan” állapotra vált.
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pl-6">
                  {[
                    { value: true, label: 'Igen, legyen beállítva' },
                    { value: false, label: 'Nem, marad a jelenlegi' },
                  ].map(opt => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setApplyAnimalFlag(opt.value)}
                      aria-pressed={applyAnimalFlag === opt.value}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                        applyAnimalFlag === opt.value
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-blue-200 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleAddMedicalEvent}
                disabled={savingMedicalEvent}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {savingMedicalEvent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Bejegyzés hozzáadása
              </button>
            </div>
            </>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimalMedicalPage;
